import type { FiscalPeriodStatus, JournalEntry, JournalEntryStatus } from "@station/contracts";
import { type LocalDate, Money, toIstanbulDate } from "@station/core";
import { cn, StatusBadge, type Tone } from "@station/ui";
import { useQuery } from "@tanstack/react-query";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api.js";
import { queryKeys } from "../../lib/query.js";

export const ENTRY_STATUS_TONE: Record<JournalEntryStatus, Tone> = {
  DRAFT: "warning",
  POSTED: "success",
  REVERSED: "neutral",
};

export const PERIOD_STATUS_TONE: Record<FiscalPeriodStatus, Tone> = {
  OPEN: "success",
  SOFT_CLOSED: "warning",
  CLOSED: "neutral",
};

export function EntryStatusBadge({ status }: { status: JournalEntryStatus }): ReactElement {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={ENTRY_STATUS_TONE[status]}>{t(`enums.journalEntryStatus.${status}`)}</StatusBadge>
  );
}

/**
 * Defter sütunlarındaki tutar: sıfır BOŞ gösterilir (borç/alacak sütunlarında okunurluk), negatif isteğe bağlı kırmızı.
 * Değer kuruş dizgesidir; `number`'a hiç çevrilmez.
 */
export function Amount({
  value,
  blankZero = false,
  colorize = false,
  className,
}: {
  value: string | bigint | null | undefined;
  blankZero?: boolean;
  colorize?: boolean;
  className?: string;
}): ReactElement | null {
  if (value === null || value === undefined || value === "") return null;
  const money = Money.fromMinor(value);
  if (blankZero && money.isZero()) return null;
  return (
    <span
      className={cn(
        "tabular whitespace-nowrap",
        colorize && money.isNegative() && "text-destructive",
        className,
      )}
    >
      {money.formatTr({ symbol: false })}
    </span>
  );
}

export function todayIstanbul(): LocalDate {
  return toIstanbulDate(new Date());
}

export function startOfYear(date: string): LocalDate {
  return `${date.slice(0, 4)}-01-01` as LocalDate;
}

/** Şirketin istasyonları (fiş satırı ve rapor boyutu). Yalnızca etkin istasyonlar seçilebilir. */
export function useCompanyStations(companyId: string) {
  return useQuery({
    queryKey: queryKeys.accounting(companyId, "stations"),
    staleTime: 300_000,
    queryFn: async () =>
      (await api.GET("/stations", { params: { query: { companyId, limit: 200 } } })).data!.items.map(
        (station) => ({
          id: station.id,
          code: station.code,
          name: station.name,
        }),
      ),
  });
}

export function useCostCenters(companyId: string) {
  return useQuery({
    queryKey: queryKeys.accounting(companyId, "cost-centers"),
    queryFn: async () =>
      (await api.GET("/accounting/cost-centers", { params: { query: { companyId } } })).data!.items,
  });
}

/** Tarayıcıda üretilen ya da sunucudan gelen içeriği dosya olarak indirir. */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = Object.assign(document.createElement("a"), { href: url, download: fileName });
  anchor.click();
  URL.revokeObjectURL(url);
}

/** base64 → Blob. `atob` ikili dizge döndürür; baytlar tek tek kopyalanır (UTF-8 çözümlemesi YAPILMAZ). */
export function base64ToBlob(contentBase64: string, mimeType: string): Blob {
  const binary = atob(contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: mimeType });
}

/**
 * Fiş listesindeki "kaynak" süzgeci: elle girilen fişler, muhasebenin kendi ürettikleri (ters kayıt, açılış, yıl sonu)
 * ve kayıt motorunu kullanan modüller. Listede olmayan bir kaynak gelirse ham adıyla gösterilir.
 */
export const JOURNAL_SOURCES = [
  "MANUAL",
  "ACCOUNTING",
  "COUNTERPARTY",
  "CASH",
  "BANK",
  "CHEQUE",
  "INVOICE",
  "FUEL",
  "LPG",
  "MARKET",
  "WASH",
  "EV",
  "CAFE",
  "RENTAL",
  "SHIFT",
  "STOCK",
] as const;

export function useJournalEntry(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.accountingEntry(id ?? "none"),
    enabled: id !== undefined,
    queryFn: async () =>
      (await api.GET("/accounting/journal-entries/{id}", { params: { path: { id: id! } } }))
        .data as JournalEntry,
  });
}

/**
 * Eşleştirme bekleyen kayıt sayısı (sekme ve menü rozeti). Uç toplam döndürmediği için ilk 100 kayıt sayılır;
 * daha fazlası varsa "100+" gösterilir. Bekleyen yoksa null (rozet çizilmez).
 */
export function usePendingExceptionCount(companyId: string | null): string | null {
  const pending = useQuery({
    queryKey: queryKeys.accounting(companyId ?? "none", "exceptions", { badge: true }),
    enabled: companyId !== null,
    staleTime: 60_000,
    queryFn: async () =>
      (
        await api.GET("/accounting/posting-exceptions", {
          params: { query: { companyId: companyId!, status: "PENDING", limit: 100 } },
        })
      ).data!,
  });
  if (!pending.data || pending.data.items.length === 0) return null;
  return `${pending.data.items.length}${pending.data.page.hasMore ? "+" : ""}`;
}
